import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiGet, apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import AuthContext from "context/AuthContext";
import OImage from "components/reusable/OImage";

const StatusBadge = ({ status }) => {
  const map = {
    accepted: "bg-green-100 text-green-800",
    shortlisted: "bg-indigo-100 text-indigo-800",
    interviewed: "bg-blue-100 text-blue-800",
    reviewing: "bg-yellow-100 text-yellow-800",
    pending: "bg-gray-100 text-gray-800",
    rejected: "bg-red-100 text-red-800",
  };
  const colorClass = map[status] || "bg-gray-100 text-gray-800";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
    >
      {status}
    </span>
  );
};

const ApplicationView = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const permission = user?.permission?.find((p) => p.manager === "careers");
  const canEdit = permission?.add || user?.role === "admin";
  const canReject = canEdit;
  const params = useParams();
  const [data, setData] = useState(null);
  const [, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiterId, setSelectedRecruiterId] = useState("");
  const [assignError, setAssignError] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionEmailBody, setRejectionEmailBody] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [form, setForm] = useState({
    status: "",
    notes: "",
    rating: "",
    interviewDate: "",
    interviewNotes: "",
    rejectionReason: "",
    rejectionReasonAr: "",
  });

  const currentRecruiterId =
    data?.recruiterId?._id || data?.recruiterId || null;
  const currentRecruiterName =
    data?.recruiterId?.name ||
    recruiters.find((r) => String(r._id) === String(currentRecruiterId))
      ?.name ||
    "";

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiGet(`${apiPath.careerApplicationById}/${params.id}`);
      const d = res?.data?.results || res?.data?.data || null;
      setData(d);
      if (d) {
        setForm({
          status: d.status || "pending",
          notes: d.notes || "",
          rating: d.rating || "",
          interviewDate: d.interviewDate
            ? new Date(d.interviewDate).toISOString().slice(0, 10)
            : "",
          interviewNotes: d.interviewNotes || "",
          rejectionReason: d.rejectionReason || "",
          rejectionReasonAr: d.rejectionReasonAr || "",
        });
        setSelectedRecruiterId(
          d?.recruiterId?._id || d?.recruiterId || ""
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRecruiters = async () => {
    try {
      const res = await apiGet(apiPath.careerRecruitersAll);
      const items = res?.data?.results || [];
      setRecruiters(Array.isArray(items) ? items : []);
    } catch {
      setRecruiters([]);
    }
  };

  useEffect(() => {
    load();
    loadRecruiters();
  }, [params.id]);

  const updateStatus = async () => {
    setUpdating(true);
    try {
      const payload = { ...form };
      const res = await apiPut(
        `${apiPath.careerApplicationUpdateStatus}/${params.id}`,
        payload,
      );
      if (res?.data?.success) {
        await load();
      }
    } finally {
      setUpdating(false);
    }
  };

  const openRejectModal = () => {
    setRejectionEmailBody("");
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    if (rejecting) return;
    setShowRejectModal(false);
    setRejectionEmailBody("");
  };

  const submitRejection = async () => {
    if (!canReject || !rejectionEmailBody.trim() || data?.status === "rejected") {
      return;
    }
    setRejecting(true);
    try {
      const res = await apiPut(
        `${apiPath.careerApplicationUpdateStatus}/${params.id}`,
        {
          status: "rejected",
          rejectionEmailBody: rejectionEmailBody.trim(),
        },
      );
      if (res?.data?.success) {
        setShowRejectModal(false);
        setRejectionEmailBody("");
        await load();
      }
    } finally {
      setRejecting(false);
    }
  };

  const assignRecruiter = async () => {
    if (!canEdit || !selectedRecruiterId) return;
    setAssigning(true);
    setAssignError("");
    try {
      const res = await apiPut(
        `${apiPath.careerApplicationAssignRecruiter}/${params.id}`,
        { recruiterId: selectedRecruiterId },
      );
      if (res?.data?.success) {
        await load();
      } else {
        setAssignError(
          res?.data?.message ||
            t("APPLICATION_ALREADY_ASSIGNED") ||
            "Application is already assigned to another recruiter"
        );
      }
    } catch (e) {
      setAssignError(
        e?.response?.data?.message ||
          t("Something went wrong") ||
          "Something went wrong"
      );
    } finally {
      setAssigning(false);
    }
  };

  const unassignRecruiter = async () => {
    if (!canEdit) return;
    setAssigning(true);
    setAssignError("");
    try {
      const res = await apiPut(
        `${apiPath.careerApplicationAssignRecruiter}/${params.id}`,
        { recruiterId: null },
      );
      if (res?.data?.success) {
        setSelectedRecruiterId("");
        await load();
      } else {
        setAssignError(res?.data?.message || "Unable to unassign recruiter");
      }
    } catch (e) {
      setAssignError(
        e?.response?.data?.message ||
          t("Something went wrong") ||
          "Something went wrong"
      );
    } finally {
      setAssigning(false);
    }
  };

  if (!data) return <div className="p-3">{t("Loading...")}</div>;

  return (
    <div className="p-3 max-w-5xl mx-auto">
      <div className="bg-white rounded-lg border p-5 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-4 min-w-0">
            <div className="shrink-0 flex flex-col items-center gap-1">
              <OImage
                src={data.profilePicture}
                alt={data.fullName || t("Profile Picture") || "Profile Picture"}
                className="w-20 h-20 rounded-full object-cover border-2 border-[#8FC150]/30"
              />
              {data.profilePicture ? (
                <a
                  className="text-xs text-blue-600 underline"
                  href={data.profilePicture }
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("View Profile Picture") || "View"}
                </a>
              ) : null}
            </div>
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-gray-900">
                {t("Application")}
              </h2>
              <div className="mt-2">
                <StatusBadge status={data.status} />
              </div>
              <div className="mt-2 text-sm text-gray-600">
                {t("CAREERS_APPLIED_AT")}:{" "}
                {new Date(data.createdAt).toLocaleString()}
              </div>
              <div className="mt-2 text-base font-semibold text-gray-900">
                {data.fullName}
              </div>
            </div>
          </div>
          {canReject && data.status !== "rejected" ? (
            <button
              type="button"
              className="px-4 py-2.5 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              onClick={openRejectModal}
            >
              {t("Reject Candidate") || "Reject"}
            </button>
          ) : null}
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
          <div>
            <strong>{t("O_EMAIL_ADDRESS")}:</strong> {data.email}
          </div>
          <div>
            <strong>{t("PHONE")}:</strong> {data.phoneNumber}
          </div>
          {data.linkedin ? (
            <div>
              <strong>{t("LinkedIn") || "LinkedIn"}:</strong>{" "}
              <a
                className="text-blue-600 underline break-all"
                href={data.linkedin}
                target="_blank"
                rel="noreferrer"
              >
                {data.linkedin}
              </a>
            </div>
          ) : null}
          {data.nationality ? (
            <div>
              <strong>{t("Nationality") || "Nationality"}:</strong>{" "}
              {data.nationality}
            </div>
          ) : null}
          {data.joinTime ? (
            <div>
              <strong>{t("Ready to join in") || "Ready to join in"}:</strong>{" "}
              {data.joinTime}
            </div>
          ) : null}
          <div>
            <strong>{t("CAREERS_CAREER")}:</strong>{" "}
            {data?.careerId?.title || data.careerTitle || "-"}
          </div>
          {data.expectedSalary?.amount ? (
            <div>
              <strong>{t("Expected Salary")}:</strong>{" "}
              {data.expectedSalary.amount} {data.expectedSalary.currency}
            </div>
          ) : null}
        </div>

        {data.coverLetter ? (
          <div className="mt-4">
            <strong className="text-[14px]">{t("Cover Letter")}</strong>{" "}
            <p className="text-sm whitespace-pre-wrap">{data.coverLetter}</p>
          </div>
        ) : null}
        {data.resume ? (
          <div className="mt-3">
            <a
              className="text-blue-600 underline"
              href={data.resume}
              target="_blank"
              rel="noreferrer"
            >
              {t("Download Resume")}
            </a>
          </div>
        ) : null}
      </div>

      {/* Questions & Answers */}
      {Array.isArray(data.answers) && data.answers.length > 0 ? (
        <div className="bg-white rounded-lg border p-5 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {t("Questions") || "Questions"}
          </h2>
          <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
            {data.answers.map((item, index) => (
              <div key={item.questionId || index} className="md:col-span-2">
                <strong>{item.question || "-"}</strong>
                {item.questionAr ? (
                  <div className="text-gray-700 mt-1" dir="rtl">
                    {item.questionAr}
                  </div>
                ) : null}
                <p className="text-sm whitespace-pre-wrap mt-1">
                  {item.answer || "-"}
                  {item.answerAr ? ` / ${item.answerAr}` : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Assign Recruiter */}
      <div className="bg-white rounded-lg border p-5 mb-6">
        <h3 className="text-base font-semibold mb-3">
          {t("Assign Recruiter") || "Assign Recruiter"}
        </h3>
        <div className="grid md:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900">
              {t("Recruiter") || "Recruiter"}
            </label>
            {currentRecruiterId ? (
              <input
                className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2.5 w-full"
                value={currentRecruiterName}
                disabled
              />
            ) : (
              <select
                className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2.5 w-full"
                value={selectedRecruiterId}
                disabled={!canEdit || assigning}
                onChange={(e) => {
                  setAssignError("");
                  setSelectedRecruiterId(e.target.value);
                }}
              >
                <option value="">
                  {t("Unassigned") || "Unassigned"}
                </option>
                {recruiters.map((recruiter) => (
                  <option key={recruiter._id} value={recruiter._id}>
                    {recruiter.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          {canEdit ? (
            <div className="flex gap-2">
              {currentRecruiterId ? (
                <button
                  type="button"
                  className="px-4 py-2.5 rounded bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                  disabled={assigning}
                  onClick={unassignRecruiter}
                >
                  {assigning
                    ? t("Saving...")
                    : t("Unassign") || "Unassign"}
                </button>
              ) : (
                <button
                  type="button"
                  className="px-4 py-2.5 rounded bg-[#8FC150] hover:bg-[#7eae43] text-white disabled:opacity-50"
                  disabled={assigning || !selectedRecruiterId}
                  onClick={assignRecruiter}
                >
                  {assigning
                    ? t("Saving...")
                    : t("Assign") || "Assign"}
                </button>
              )}
            </div>
          ) : null}
        </div>
        {assignError ? (
          <p className="text-sm text-red-600 mt-2">{assignError}</p>
        ) : null}
      </div>

      {/* Update Status */}
      <div className="bg-white rounded-lg border p-5">
        <h3 className="text-base font-semibold mb-3">{t("Update Status")}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900">
              {t("O_STATUS")}
            </label>
            <select
              className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2.5 w-full"
              value={form.status}
              disabled={!canEdit}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              {[
                "pending",
                "reviewing",
                "shortlisted",
                "interviewed",
                "accepted",
                "rejected",
              ].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900">
              {t("Rating")}
            </label>
            <input
              className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2.5 w-full"
              type="number"
              disabled={!canEdit}
              min="0"
              max="5"
              value={form.rating}
              onChange={(e) => setForm({ ...form, rating: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block mb-2 text-sm font-medium text-gray-900">
              {t("Notes")}
            </label>
            <textarea
              className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2.5 w-full"
              rows={3}
              disabled={!canEdit}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900">
              {t("Interview Date")}
            </label>
            <input
              className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2.5 w-full"
              type="date"
              disabled={!canEdit}
              value={form.interviewDate}
              onChange={(e) =>
                setForm({ ...form, interviewDate: e.target.value })
              }
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900">
              {t("Interview Notes")}
            </label>
            <input
              className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2.5 w-full"
              disabled={!canEdit}
              value={form.interviewNotes}
              onChange={(e) =>
                setForm({ ...form, interviewNotes: e.target.value })
              }
            />
          </div>
          {form.status === "rejected" ? (
            <>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-900">
                  {t("Rejection Reason")}
                </label>
                <input
                  className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2.5 w-full"
                  disabled={!canEdit}
                  value={form.rejectionReason}
                  onChange={(e) =>
                    setForm({ ...form, rejectionReason: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-900">
                  {t("Rejection Reason (Ar)")}
                </label>
                <input
                  className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2.5 w-full"
                  disabled={!canEdit}
                  value={form.rejectionReasonAr}
                  onChange={(e) =>
                    setForm({ ...form, rejectionReasonAr: e.target.value })
                  }
                />
              </div>
            </>
          ) : null}
        </div>
        <div className="mt-4 flex justify-end">
          {!canEdit ? null : (
            <button
              className="px-4 py-2.5 rounded bg-[#8FC150] hover:bg-[#8FC150] text-white"
              onClick={updateStatus}
              disabled={updating}
            >
              {updating ? t("Saving...") : t("Save")}
            </button>
          )}
        </div>
      </div>

      {showRejectModal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative my-6 w-full max-w-lg mx-auto">
              <div className="sm:py-4 sm:px-2 py-8 px-7">
                <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                  <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                    <h3 className="text-xl font-semibold">
                      {t("Reject Candidate") || "Reject Candidate"}
                    </h3>
                    <button
                      type="button"
                      className="ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none"
                      onClick={closeRejectModal}
                      disabled={rejecting}
                    >
                      <span className="text-[#B8BBBF] text-4xl" title="Close">
                        ×
                      </span>
                    </button>
                  </div>
                  <div className="relative p-6 flex-auto">
                    <label className="block mb-2 text-sm font-medium text-gray-900">
                      {t("Rejection Email") || "Rejection Email"}
                    </label>
                    <textarea
                      className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2.5 w-full"
                      rows={8}
                      value={rejectionEmailBody}
                      disabled={rejecting}
                      placeholder={
                        t("Write the rejection mail") ||
                        "Write the rejection mail"
                      }
                      onChange={(e) => setRejectionEmailBody(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center justify-end gap-3 p-6 border-t border-solid border-slate-200 rounded-b">
                    <button
                      type="button"
                      className="px-4 py-2.5 rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
                      onClick={closeRejectModal}
                      disabled={rejecting}
                    >
                      {t("O_CANCEL") || "Cancel"}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2.5 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                      disabled={rejecting || !rejectionEmailBody.trim()}
                      onClick={submitRejection}
                    >
                      {rejecting
                        ? t("Saving...") || "Saving..."
                        : t("Reject Candidate") || "Reject"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </div>
  );
};

export default ApplicationView;
